import React, { type JSX } from "react";
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from "@heroicons/react/24/outline";
import { newTabAwareClick } from "./NewChatLink";
import logoImage from "../../../assets/logo.png";

interface SidebarBrandProps {
  isCollapsed: boolean;
  onCollapseToggle?: (collapsed: boolean) => void;
  onStartProject?: () => void;
}

function ExpandButton({ onExpand }: { onExpand: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 mx-auto"
      aria-label="Expand sidebar"
      title="Expand sidebar"
      onClick={(e) => {
        e.stopPropagation();
        onExpand();
      }}
    >
      <ChevronDoubleRightIcon className="h-5 w-5" style={{ color: "#234E81" }} />
    </button>
  );
}

/** Brand/header block: logo (starts a new chat) plus the collapse affordance. */
export default function SidebarBrand({
  isCollapsed,
  onCollapseToggle,
  onStartProject,
}: SidebarBrandProps): JSX.Element {
  return (
    <div className="px-4 py-2.5 border-b border-gray-200">
      <div className="flex items-center justify-between min-h-[3.5rem]">
        {isCollapsed ? (
          <ExpandButton onExpand={() => onCollapseToggle && onCollapseToggle(false)} />
        ) : (
          <>
            <a
              href={window.location.origin}
              onClick={(e) => newTabAwareClick(e, onStartProject)}
              className="flex items-center space-x-3 hover:opacity-70 transition-opacity duration-200 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
              aria-label="Start new chat"
            >
              <img src={logoImage} alt="DealOS Logo" className="h-14 w-14 object-contain" />
              <span className="text-gray-300 text-2xl font-light">|</span>
              <span className="text-xl font-cvcBody font-normal text-cvc-text">DealOS</span>
            </a>

            {/* Desktop collapse toggle button - only shown when expanded */}
            {onCollapseToggle && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onCollapseToggle(true);
                }}
                className="p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200"
                aria-label="Collapse sidebar"
                title="Collapse sidebar"
              >
                <ChevronDoubleLeftIcon className="h-5 w-5" style={{ color: "#234E81" }} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
